import React, { useEffect, useState, useRef } from 'react';
import authService from '../../api-authorization/AuthorizeService';
import RequestOverlay from '../../components/RequestOverlay';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, Tooltip, Select, MenuItem, FormControl } from '@mui/material';
import styles from './AdminConfigHealth.module.css';

function getDateCellStyle(date) {
  if (!date) return {};
  const now = new Date();
  const dt = typeof date === 'string' ? new Date(date) : date;
  const isToday = dt.toDateString() === now.toDateString();
  const diffDays = (now - dt) / (1000 * 60 * 60 * 24);
  if (isToday) return { background: '#43a047', color: '#fff' }; // green
  if (diffDays <= 7) return { background: '#ff9800', color: '#222' }; // orange
  return { background: '#d32f2f', color: '#fff' }; // red
}

function getWarningsCellStyle(count) {
  if (count == null) return {};
  if (count <= 5) return { background: '#43a047', color: '#fff' };
  if (count <= 10) return { background: '#ff9800', color: '#222' };
  return { background: '#d32f2f', color: '#fff' };
}

function getTotalHerbicideCellStyle(gallons) {
  if (gallons == null || isNaN(gallons)) return {};
  if (gallons <= 5) return { background: '#d32f2f', color: '#fff' };
  if (gallons <= 10) return { background: '#ff9800', color: '#222' };
  if (gallons > 10) return { background: '#43a047', color: '#fff' };
  return {};
}

function getTotalDistanceCellStyle(miles) {
  if (miles == null || isNaN(miles)) return {};
  if (miles <= 0.5) return { background: '#d32f2f', color: '#fff' };
  if (miles <= 2) return { background: '#ff9800', color: '#222' };
  if (miles > 2) return { background: '#43a047', color: '#fff' };
  return {};
}

function getErrorsCellStyle(count) {
  if (count == null) return {};
  if (count === 0) return { background: '#43a047', color: '#fff' };
  if (count <= 2) return { background: '#ff9800', color: '#222' };
  return { background: '#d32f2f', color: '#fff' };
}

function getWeedItModeDisplay(pwmMode) {
  if (pwmMode === '') return { label: 'Weed-It Off', color: '#FF9999' };
  if (!pwmMode) return { label: '', color: undefined };
  const mode = pwmMode.trim().toLowerCase();
  switch (mode) {
    case 'off': return { label: 'Spot Plain', color: '#bdbdbd' };
    case 'spot': return { label: 'Spot Spraying', color: '#1976d2' };
    case 'coverage': return { label: 'Broadcast', color: '#d32f2f' };
    case 'dual': return { label: 'Dual Rate', color: '#ff9800' };
    case 'plain spot-in-crop': return { label: 'SiC Plain', color: '#6d4c41' };
    case 'spot spot-in-crop': return { label: 'SiC Spot', color: '#00897b' };
    case 'cover spot-in-crop': return { label: 'SiC Broadcast', color: '#8bc34a' };
    case 'dual spot-in-crop': return { label: 'SiC Dual Rate', color: '#f44336' };
    default: return { label: pwmMode, color: '#bdbdbd' };
  }
}

const handleGoToSprayerDetails = (sprayer) => {
  if (!sprayer || !sprayer.tractorName || !sprayer.tractorId) return;
  const url = `/precision-spray/${sprayer.tractorName.replace(/ /g, "-")}/${sprayer.tractorId}`;
  window.open(url, "_blank");
};

const columns = [
  { key: 'clientName', label: 'Client', description: 'The client name.' },
  { key: 'tractorName', label: 'Sprayer', description: 'The tractor or sprayer name.' },
  {
    key: 'latestRunStartTimeUtc',
    label: 'Latest Run Start',
    description: 'The start time of the most recent run (browser local time).',
    legend: [
      { color: '#43a047', label: 'Today: green' },
      { color: '#ff9800', label: 'Within 1 week: orange' },
      { color: '#d32f2f', label: 'Older: red' }
    ]
  },
  {
    key: 'latestHeartbeatTimeUtc',
    label: 'Latest Heartbeat',
    description: 'The most recent heartbeat received from the device (browser local time).',
    legend: [
      { color: '#43a047', label: 'Today: green' },
      { color: '#ff9800', label: 'Within 1 week: orange' },
      { color: '#d32f2f', label: 'Older: red' }
    ]
  },
  {
    key: 'herbicideSavedPercent',
    label: 'Product Saved (%)',
    description: 'Percent of product saved compared to broadcast application.',
    legend: [
      { color: '#d32f2f', label: '< 0: red' },
      { color: '#ff9800', label: '0-40: orange' },
      { color: '#43a047', label: '> 40: green' },
    ]
  },
  {
    key: 'totalPesticideSprayedGallons',
    label: 'Product Applied (gal)',
    description: 'Total gallons of product sprayed in the latest run.',
    legend: [
      { color: '#d32f2f', label: '0-5: red' },
      { color: '#ff9800', label: '5-10: orange' },
      { color: '#43a047', label: '10+: green' }
    ]
  },
  {
    key: 'totalDistanceMiles',
    label: 'Total Distance (mi)',
    description: 'Total miles traveled in the latest run.',
    legend: [
      { color: '#d32f2f', label: '0-0.5: red' },
      { color: '#ff9800', label: '0.5-2: orange' },
      { color: '#43a047', label: '2+: green' }
    ]
  },
  {
    key: 'weedItPwmMode',
    label: 'Weed-It Mode',
    description: 'Most recent Weed-It spray mode for the device.',
    legend: [
      { color: '#e0e0e0', label: 'No Data' },
      { color: '#bdbdbd', label: 'Spot Plain' },
      { color: '#1976d2', label: 'Spot Spraying' },
      { color: '#d32f2f', label: 'Broadcast' },
      { color: '#ff9800', label: 'Dual Rate' },
      { color: '#6d4c41', label: 'SiC Plain' },
      { color: '#00897b', label: 'SiC Spot' },
      { color: '#8bc34a', label: 'SiC Broadcast' },
      { color: '#f44336', label: 'SiC Dual Rate' }
    ]
  },
  {
    key: 'warnings',
    label: 'Warnings',
    description: 'Warning messages within the last 24 hours from the device.',
    legend: [
      { color: '#43a047', label: '0-5: green' },
      { color: '#ff9800', label: '6-10: orange' },
      { color: '#d32f2f', label: '11+: red' },
    ]
  },
  {
    key: 'errors',
    label: 'Errors',
    description: 'Error messages within the last 24 hours from the device.',
    legend: [
      { color: '#43a047', label: '0: green' },
      { color: '#ff9800', label: '1-2: orange' },
      { color: '#d32f2f', label: '3+: red' },
    ]
  },
  {
    key: 'latestCpuTempC',
    label: 'CPU Temp (°C)',
    description: 'Most recent CPU temperature reported by the device.',
    legend: [
      { color: '#43a047', label: '< 60: green' },
      { color: '#ffeb3b', label: '60-70: yellow', border: '1px solid #888' },
      { color: '#ff9800', label: '70-80: orange' },
      { color: '#d32f2f', label: '80+: red' },
    ]
  },
  {
    key: 'hasScrambledHeartbeats',
    label: 'Scrambled Heartbeats',
    description: 'Indicates if scrambled heartbeats were detected in the latest run.',
    legend: [
      { color: '#d32f2f', label: 'Yes: red' },
      { color: '#43a047', label: 'No: green' },
    ]
  },
];

function LegendTooltip({ legend }) {
  return (
    <div style={{ padding: 8, fontSize: 15, minWidth: 120 }}>
      {legend.map((item, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <span style={{
            display: 'inline-block',
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: item.color,
            marginRight: 8,
            border: item.border || '1px solid #ccc',
            flexShrink: 0
          }} />
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function getHerbicideCellStyle(percent) {
  if (percent == null || isNaN(percent)) return {};
  if (percent < 0) return { background: '#d32f2f', color: '#fff' };
  if (percent <= 40) return { background: '#ff9800', color: '#222' };
  if (percent > 40) return { background: '#43a047', color: '#fff' };
  return {};
}

function getCpuTempCellStyle(temp) {
  if (temp == null || isNaN(temp)) return {};
  if (temp < 60) return { background: '#43a047', color: '#fff' };
  if (temp < 70) return { background: '#ffeb3b', color: '#222' };
  if (temp < 80) return { background: '#ff9800', color: '#222' };
  if (temp >= 80) return { background: '#d32f2f', color: '#fff' };
  return {};
}

function parseUtcDate(dateStr) {
  if (!dateStr) return null;
  // If already ISO with Z or offset, just use it
  if (dateStr.endsWith('Z') || /[+-]\d\d:?\d\d$/.test(dateStr)) {
    return new Date(dateStr);
  }
  // If format is 'YYYY-MM-DDTHH:mm:ss(.SSS)?' with no Z/offset, treat as UTC
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(dateStr)) {
    return new Date(dateStr + 'Z');
  }
  // If format is 'YYYY-MM-DD HH:mm:ss', convert to ISO
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateStr)) {
    return new Date(dateStr.replace(' ', 'T') + 'Z');
  }
  // Fallback
  return new Date(dateStr);
}

// Highlight style for changed cells
const HIGHLIGHT_CLASS = 'cell-changed';
const HIGHLIGHT_DURATION = 1200; // ms

// Add highlight CSS to the document head if not already present
if (typeof window !== 'undefined' && !document.getElementById('admin-config-health-highlight-style')) {
  const style = document.createElement('style');
  style.id = 'admin-config-health-highlight-style';
  style.innerHTML = `
    .cell-changed {
      animation: cellChangedFade 1.2s;
      background: #fff59d !important;
      transition: background 1.2s;
    }
    @keyframes cellChangedFade {
      0% { background: #fff59d; }
      100% { background: inherit; }
    }
  `;
  document.head.appendChild(style);
}

const AdminConfigHealth = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true); // used only for initial load
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [changedCells, setChangedCells] = useState(new Set());
  const prevDataRef = useRef([]);

  useEffect(() => {
    let isMounted = true;
    let firstLoad = true;
    const fetchData = async () => {
      if (firstLoad) setLoading(true);
      setError(false);
      try {
        const token = await authService.getAccessToken();
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/deviceHealth`, {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch');
        const json = await res.json();
        if (isMounted) {
          // Compare new data to previous data for cell changes
          const prevData = prevDataRef.current;
          const newChangedCells = new Set();
          json.forEach((row, idx) => {
            const prevRow = prevData.find(r => r.deviceId === row.deviceId) || prevData[idx];
            if (prevRow) {
              columns.forEach(col => {
                const key = col.key;
                let prevVal = prevRow[key];
                let newVal = row[key];
                if (Array.isArray(prevVal) && Array.isArray(newVal)) {
                  if (prevVal.length !== newVal.length) {
                    newChangedCells.add(`${row.deviceId ?? idx}:${key}`);
                  }
                } else if (prevVal !== newVal) {
                  newChangedCells.add(`${row.deviceId ?? idx}:${key}`);
                }
              });
            }
          });
          setChangedCells(newChangedCells);
          setData(json);
          prevDataRef.current = json;
          if (newChangedCells.size > 0) {
            setTimeout(() => setChangedCells(new Set()), HIGHLIGHT_DURATION);
          }
        }
      } catch {
        if (isMounted) setError(true);
      } finally {
        if (firstLoad && isMounted) {
          setLoading(false);
          setInitialLoading(false);
          firstLoad = false;
        }
      }
    };
    fetchData();
    const interval = setInterval(() => {
      firstLoad = false;
      fetchData();
    }, 10000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Get unique client names from data
  const clientNames = React.useMemo(() => {
    const names = data.map(d => d.clientName).filter(Boolean);
    return Array.from(new Set(names));
  }, [data]);

  // Filter data by selected client
  let filteredData = selectedClient ? data.filter(d => d.clientName === selectedClient) : data;

  // Sort data
  if (sortBy) {
    filteredData = [...filteredData].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
      if (bValue == null) return sortDirection === 'asc' ? 1 : -1;
      // For string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      // For date comparison
      if (sortBy === 'latestRunStartTimeUtc' || sortBy === 'latestHeartbeatTimeUtc') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      // For boolean
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortDirection === 'asc' ? (aValue === bValue ? 0 : aValue ? 1 : -1) : (aValue === bValue ? 0 : aValue ? -1 : 1);
      }
      // For number
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }

  const handleSort = (colKey) => {
    if (sortBy === colKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(colKey);
      setSortDirection('asc');
    }
  };

  return (
    <Box className={styles.container} sx={{ pb: { xs: 3, sm: 2 } }}>
      <RequestOverlay
        isLoading={initialLoading}
        id="loading-overlay"
        errorTitle="Your data couldn't be loaded."
        errorSubtitle="Please contact an administrator."
      />
      <Typography variant="h4" sx={{ pb: 2 }}>
        Device Health
      </Typography>
      <Box sx={{ mb: 2, maxWidth: 320 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Client
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={selectedClient}
            onChange={e => setSelectedClient(e.target.value)}
            displayEmpty
          >
            <MenuItem value=""><em>All Clients</em></MenuItem>
            {clientNames.map(name => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map(col => {
                const isSorted = sortBy === col.key;
                const sortIndicator = isSorted ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '';
                const cellContent = (
                  <span
                    style={{ userSelect: 'none', cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}{sortIndicator}
                  </span>
                );
                return (
                  <TableCell
                    key={col.key}
                    align="center"
                    sx={col.legend || col.description ? { cursor: 'help', fontWeight: 600 } : undefined}
                  >
                    {(col.legend || col.description) ? (
                      <Tooltip
                        title={
                          <div>
                            {col.description && <div style={{ marginBottom: col.legend ? 8 : 0 }}>{col.description}</div>}
                            {col.legend && <LegendTooltip legend={col.legend} />}
                          </div>
                        }
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: '#fff',
                              color: '#222',
                              boxShadow: 3,
                              borderRadius: 2,
                              fontSize: 15,
                              p: 1.5,
                              border: '1px solid #bbb',
                              maxWidth: 320
                            }
                          }
                        }}
                      >
                        {cellContent}
                      </Tooltip>
                    ) : cellContent}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row, idx) => (
              <TableRow key={row.deviceId || idx}>
                {columns.map(col => {
                  let value = row[col.key];
                  const cellKey = `${row.deviceId ?? idx}:${col.key}`;
                  const highlightClass = changedCells.has(cellKey) ? HIGHLIGHT_CLASS : '';
                  if (col.key === 'warnings') {
                    const count = Array.isArray(row.warnings) ? row.warnings.length : 0;
                    return (
                      <TableCell key={col.key} className={highlightClass} style={getWarningsCellStyle(count)}>
                        <Tooltip
                          title={count > 0 ? (
                            <div style={{ whiteSpace: 'pre-line' }}>
                              {row.warnings.map((w, i) => <div key={i}>{w}</div>)}
                            </div>
                          ) : 'No warnings'}
                          arrow
                          enterDelay={300}
                          leaveDelay={200}
                          disableInteractive
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: 16,
                                maxWidth: 'none',
                                bgcolor: '#fff',
                                color: '#222',
                                boxShadow: 3,
                                borderRadius: 2,
                                p: 1.5,
                                border: '1px solid #bbb'
                              }
                            }
                          }}
                        >
                          <span style={{ cursor: count > 0 ? 'pointer' : 'default', display: 'inline-block', minWidth: 24 }}>{count}</span>
                        </Tooltip>
                      </TableCell>
                    );
                  }
                  if (col.key === 'errors') {
                    const count = Array.isArray(row.errors) ? row.errors.length : 0;
                    return (
                      <TableCell key={col.key} className={highlightClass} style={getErrorsCellStyle(count)}>
                        <Tooltip
                          title={count > 0 ? (
                            <div style={{ whiteSpace: 'pre-line' }}>
                              {row.errors.map((e, i) => <div key={i}>{e}</div>)}
                            </div>
                          ) : 'No errors'}
                          arrow
                          enterDelay={300}
                          leaveDelay={200}
                          disableInteractive
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: 16,
                                maxWidth: 'none',
                                bgcolor: '#fff',
                                color: '#222',
                                boxShadow: 3,
                                borderRadius: 2,
                                p: 1.5,
                                border: '1px solid #bbb'
                              }
                            }
                          }}
                        >
                          <span style={{ cursor: count > 0 ? 'pointer' : 'default', display: 'inline-block', minWidth: 24 }}>{count}</span>
                        </Tooltip>
                      </TableCell>
                    );
                  }
                  if (col.key === 'tractorName') {
                    return (
                      <TableCell key={col.key} className={highlightClass}>
                        <span
                          style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() => handleGoToSprayerDetails(row)}
                          title="Open sprayer details"
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter') handleGoToSprayerDetails(row); }}
                          role="button"
                        >
                          {value}
                        </span>
                      </TableCell>
                    );
                  }
                  if (col.key === 'weedItPwmMode') {
                    const { label, color } = getWeedItModeDisplay(value);
                    return (
                      <TableCell key={col.key} className={highlightClass} style={{ background: color, color: '#fff' }}>
                        {label}
                      </TableCell>
                    );
                  }
                  if (col.key === 'herbicideSavedPercent') {
                    const isBroadcast = row.weedItPwmMode?.trim().toLowerCase().includes('coverage');
                    return (
                      <TableCell key={col.key} className={highlightClass} style={isBroadcast ? undefined : getHerbicideCellStyle(value)}>
                        {isBroadcast ? 'N/A' : value == null ? '' : value.toFixed(1)}
                      </TableCell>
                    );
                  }
                  if (col.key === 'totalPesticideSprayedGallons') {
                    return (
                      <TableCell key={col.key} className={highlightClass} style={getTotalHerbicideCellStyle(value)}>
                        {value == null ? '' : value.toFixed(2)}
                      </TableCell>
                    );
                  }
                  if (col.key === 'totalDistanceMiles') {
                    return (
                      <TableCell key={col.key} className={highlightClass} style={getTotalDistanceCellStyle(value)}>
                        {value == null ? '' : value.toFixed(2)}
                      </TableCell>
                    );
                  }
                  if (col.key === 'latestCpuTempC') {
                    return (
                      <TableCell key={col.key} className={highlightClass} style={getCpuTempCellStyle(value)}>
                        {value == null ? '' : value.toFixed(1)}
                      </TableCell>
                    );
                  }
                  if (col.key === 'hasScrambledHeartbeats') {
                    const style = value ? { background: '#d32f2f', color: '#fff' } : { background: '#43a047', color: '#fff' };
                    return (
                      <TableCell key={col.key} className={highlightClass} style={style}>
                        {value ? 'Yes' : 'No'}
                      </TableCell>
                    );
                  }
                  if (col.key === 'latestRunStartTimeUtc') {
                    const dt = parseUtcDate(value);
                    return (
                      <TableCell key={col.key} className={highlightClass} style={getDateCellStyle(dt)}>
                        {dt ? dt.toLocaleString(undefined, { timeZoneName: 'short' }) : ''}
                      </TableCell>
                    );
                  }
                  if (col.key === 'latestHeartbeatTimeUtc') {
                    const dt = parseUtcDate(value);
                    return (
                      <TableCell key={col.key} className={highlightClass} style={getDateCellStyle(dt)}>
                        {dt ? dt.toLocaleString(undefined, { timeZoneName: 'short' }) : ''}
                      </TableCell>
                    );
                  }
                  if (typeof value === 'number') {
                    return (
                      <TableCell key={col.key} className={highlightClass}>{value.toFixed(2)}</TableCell>
                    );
                  }
                  return <TableCell key={col.key} className={highlightClass}>{value ?? ''}</TableCell>;
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdminConfigHealth;